/* global BigInt */
import { formatDclMoney, getDclPresentation, dclSourceLabel } from './integratedDclPresentation';

export function compareInsuranceMoney(left, right) {
  const parse = value => /^([A-Z]{3})\s+(-?\d[\d,]*(?:\.\d+)?)/.exec(String(value || ''));
  const a = parse(left), b = parse(right);
  if (!a || !b) return a ? -1 : b ? 1 : String(left || '').localeCompare(String(right || ''));
  // Group different currencies alphabetically; never imply an FX comparison.
  if (a[1] !== b[1]) return a[1].localeCompare(b[1]);
  const [aw, af = ''] = a[2].replace(/,/g, '').split('.');
  const [bw, bf = ''] = b[2].replace(/,/g, '').split('.');
  const places = Math.max(af.length, bf.length);
  const an = BigInt(`${aw}${af.padEnd(places, '0')}`), bn = BigInt(`${bw}${bf.padEnd(places, '0')}`);
  return an < bn ? -1 : an > bn ? 1 : 0;
}

export function insuredLimitText(assessment) {
  const record = assessment?.insuredLimit;
  if (!record) return 'Unknown';
  const limit = record.amount !== null && record.amount !== undefined ? { amount: record.amount, currency: record.currency } : record.lastKnownAmount;
  const money = formatDclMoney(limit, '');
  const status = record.state || 'unknown';
  return money ? `${money}${status === 'known' ? '' : ` · ${status.replace(/_/g, ' ')} (${status === 'not_yet_effective' ? 'not current' : 'historical'})`}` : status === 'unknown' ? 'Unknown' : status.replace(/_/g, ' ');
}

export const insuranceExportHeaders = ['Insured Limit', 'Insured Limit status', 'Insured Limit source', 'DCL status', 'DCL limit', 'DCL policy', 'DCL policy version', 'DCL assessed at', 'DCL source revision'];
export function insuranceExportValues(assessment, options = {}) {
  const view = getDclPresentation(assessment, options);
  return [insuredLimitText(assessment), assessment?.insuredLimit?.state || 'unknown',
    (assessment?.insuredLimit?.sources || []).map(dclSourceLabel).join('; '), view.label, view.limitText,
    assessment?.policyId || '', assessment?.policyVersion ?? '', assessment?.assessmentAt || '', assessment?.sourceRevision || ''];
}

// The PDF report's insurance appendix uses the same current server assessment as the screen.
// It adds provenance, not a recalculated recommendation or inferred cover decision.
export async function appendDclAssessmentToPdf(bytes, assessment, { companyName = '' } = {}) {
  const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib');
  const pdf = await PDFDocument.load(bytes);
  await pdf.attach(new Uint8Array(new TextEncoder().encode(JSON.stringify(assessment, null, 2))), 'insurance-assessment.json', {
    mimeType: 'application/json', description: 'Exact server assessment, original Unicode clause evidence and source references',
  });
  const font = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);
  const view = getDclPresentation(assessment);
  let page, y;
  const ascii = value => String(value ?? '').replace(/[^\x20-\x7E\n]/g, ' ');
  const newPage = () => { page = pdf.addPage([595, 842]); y = 795; };
  const draw = (text, heading = false) => {
    const words = ascii(text).split(/\s+/); let line = '';
    const emit = value => {
      if (y < 52) newPage();
      page.drawText(value, { x: 42, y, size: heading ? 12 : 10, font: heading ? bold : font, color: rgb(.15, .21, .33) }); y -= heading ? 23 : 16;
    };
    for (const word of words) {
      const next = line ? `${line} ${word}` : word;
      if (font.widthOfTextAtSize(next, heading ? 12 : 10) > 510 && line) { emit(line); line = word; } else line = next;
    }
    if (line) emit(line);
  };
  newPage();
  draw('Insurance policy assessment', true); draw(companyName || assessment?.companyId || 'Company');
  draw(`Insured Limit: ${insuredLimitText(assessment)}`);
  draw(`Insured Limit status: ${assessment?.insuredLimit?.state || 'unknown'}`);
  draw(`DCL status: ${view.label}`); draw(`DCL limit: ${view.limitText}`);
  draw(view.policyLabel); draw(`Assessed at: ${assessment?.assessmentAt || 'Not assessed'}`);
  draw(`Source revision: ${assessment?.sourceRevision || 'Unavailable'}`); draw(view.reason);
  for (const [label, clauses] of [['Met', assessment?.metClauses], ['Failed', assessment?.failedClauses], ['Unknown', assessment?.unknownClauses]]) {
    for (const clause of clauses || []) {
      draw(`${label}: ${clause.label || clause.id || clause.reason || ''}`, true);
      draw(clause.reason || '');
      if (clause.citation) draw(`${clause.citation.location || ''}: ${clause.citation.quote || ''}`);
      for (const source of clause.sourceRefs || []) draw(dclSourceLabel(source));
    }
  }
  draw(view.disclaimer); draw('Internal risk recommendation, insurer-approved limits and DCL policy assessments remain separate. No currency conversion has been applied.');
  draw('The attached insurance-assessment.json preserves the complete assessment, including original Unicode source wording.');
  return pdf.save();
}
